import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Grid,
  Typography,
  withStyles,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import ViewListIcon from '@material-ui/icons/ViewList';
import { getStudentAvatarUrl, testVersions } from '../../../StudentRoster/+store/studentRoster';
import { Subjects } from '../../+store/aToF/aToF.constants';

const styles = (theme) => ({
  modal: {
    position: 'absolute',
    background: 'linear-gradient(0deg, rgba(175, 228, 236, 0.65) 0%, rgba(255, 255, 255, 0) 100%), #FFFFFF;',
    boxShadow: theme.shadows[5],
    padding: theme.spacing.unit * 4,
    outline: 'none',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    maxHeight: 600,
    overflowY: 'scroll',
  },
  icon: {
    width: 50,
    height: 50,
  },
  student: {
    minWidth: '20%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.unit * 2,
  },
  toggleButton: {
    marginLeft: theme.spacing.unit,
  },
  versionToggle: {
    marginLeft: theme.spacing.unit,
    height: 28,
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  table: {
    minWidth: 400,
  },
  tableCell: {
    padding: '8px 16px',
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  version: {
    fontSize: '0.75rem',
    color: 'rgba(0, 0, 0, 0.6)',
  },
});

const getStudentProfilePicture = (student, avatars, iconClass) => {
  const avatarUrl = getStudentAvatarUrl(student, avatars);
  const studentPhotoUrl = student && student.photoUrl && student.photoUrl.small ? student.photoUrl.small : avatarUrl;
  if (studentPhotoUrl) {
    return <img src={studentPhotoUrl} alt="Logo" className={iconClass} />;
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      <path d="M0 0h24v24H0z" fill="none" />
    </svg>
  );
};

const StudentsListComponent = ({
  classes,
  handleClose,
  studentsList,
  students,
  studentVersions,
  avatars,
  rowLabel,
  indicatorLabel,
}) => {
  const [viewMode, setViewMode] = React.useState('grid');
  const [showVersion, setShowVersion] = React.useState(false);

  if (!studentsList || !Object.entries(studentsList).length || students == null) return null;

  const hasVersions = studentVersions != null && Object.keys(studentVersions).length > 0;

  // Friendly, comma-joined test version(s) for a student (e.g. "STAAR" or
  // "STAAR, STAAR Alt 2"). A student can have more than one in aggregated views.
  // EOC tests are recorded by course rather than version, so an 8th grader's
  // Algebra I EOC shows "Algebra I" instead of an indistinct "STAAR".
  const getStudentVersion = (id) => {
    const raw = studentVersions && studentVersions[id];
    if (!raw || !raw.length) return '';
    return raw.map((v) => testVersions[v] || Subjects[v] || v).join(', ');
  };

  // Build dynamic title from indicator and row labels (e.g., "Asian - Meets")
  const getModalTitle = () => {
    if (indicatorLabel && rowLabel) {
      return `${indicatorLabel} - ${rowLabel}`;
    }
    return 'Students';
  };

  const getStudentLastName = (id) => (students[id] && students[id].lastName ? students[id].lastName : '');
  const getStudentFirstName = (id) => (students[id] && students[id].firstName ? students[id].firstName : '');
  const getStudentLocalId = (id) => (students[id] && students[id].localId ? students[id].localId : id);

  const getStudentNameShort = (id) =>
    `${getStudentFirstName(id) || 'N/A'} ${getStudentLastName(id) ? getStudentLastName(id).substring(0, 1) : ''}`;

  const getStudentNameFull = (id) => `${getStudentLastName(id)}, ${getStudentFirstName(id)}`;

  const getAvatar = (student, iconClass) => getStudentProfilePicture(student, avatars, iconClass);

  // Sort students alphabetically by last name, then first name
  const sortedStudents = Object.entries(studentsList).sort((a, b) => {
    const lastNameA = getStudentLastName(a[0]).toLowerCase();
    const lastNameB = getStudentLastName(b[0]).toLowerCase();
    if (lastNameA !== lastNameB) {
      return lastNameA.localeCompare(lastNameB);
    }
    const firstNameA = getStudentFirstName(a[0]).toLowerCase();
    const firstNameB = getStudentFirstName(b[0]).toLowerCase();
    return firstNameA.localeCompare(firstNameB);
  });

  const renderGridView = () => (
    <Grid container justify="center" spacing={16}>
      {sortedStudents.map((item, idx) => (
        <Grid key={idx} item className={classes.student}>
          <Grid container direction="column" justify="center" alignItems="center">
            {getAvatar(students[item[0]], classes.icon)}
            {getStudentNameShort(item[0])}
            {item[1] > 1 ? ` (${item[1]})` : ''}
            <div>{getStudentLocalId(item[0])}</div>
            {showVersion && hasVersions && <div className={classes.version}>{getStudentVersion(item[0])}</div>}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );

  const renderTableView = () => (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Name</TableCell>
          <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Local ID</TableCell>
          {showVersion && hasVersions && (
            <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Test Version</TableCell>
          )}
          <TableCell className={`${classes.tableCell} ${classes.tableHeader}`} align="center">
            Count
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedStudents.map((item, idx) => (
          <TableRow key={idx} hover>
            <TableCell className={classes.tableCell}>{getStudentNameFull(item[0])}</TableCell>
            <TableCell className={classes.tableCell}>{getStudentLocalId(item[0])}</TableCell>
            {showVersion && hasVersions && (
              <TableCell className={classes.tableCell}>{getStudentVersion(item[0])}</TableCell>
            )}
            <TableCell className={classes.tableCell} align="center">
              {item[1]}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Modal aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description" open onClose={handleClose}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <Typography variant="h6" id="modal-title">
            {getModalTitle()} ({sortedStudents.length})
          </Typography>
          <div>
            {hasVersions && (
              <Tooltip title={showVersion ? 'Hide test version' : 'Show test version'}>
                <Chip
                  label="Test Version"
                  className={classes.versionToggle}
                  onClick={() => setShowVersion((prev) => !prev)}
                  color={showVersion ? 'primary' : 'default'}
                  variant={showVersion ? 'default' : 'outlined'}
                />
              </Tooltip>
            )}
            <Tooltip title="Grid View">
              <IconButton
                className={classes.toggleButton}
                onClick={() => setViewMode('grid')}
                color={viewMode === 'grid' ? 'primary' : 'default'}
                size="small"
              >
                <ViewModuleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Table View (for copy/paste)">
              <IconButton
                className={classes.toggleButton}
                onClick={() => setViewMode('table')}
                color={viewMode === 'table' ? 'primary' : 'default'}
                size="small"
              >
                <ViewListIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        {viewMode === 'grid' ? renderGridView() : renderTableView()}
      </div>
    </Modal>
  );
};

StudentsListComponent.propTypes = {
  handleClose: PropTypes.func.isRequired,
  students: PropTypes.object,
  studentsList: PropTypes.object,
  studentVersions: PropTypes.object,
  classes: PropTypes.any,
  avatars: PropTypes.object,
  rowLabel: PropTypes.string,
  indicatorLabel: PropTypes.string,
};

export default withStyles(styles)(StudentsListComponent);
